/**
 * Scheduler API service
 * Communicates with backend /api/scheduler/* endpoints
 */
import axios from '../api/axios';

export interface SectionStudent {
  StudentNo: string;
  DB: string;
  first_name?: string;
  last_name?: string;
  preferred_slot: string | null;
}

export interface UnmatchedStudent {
  StudentNo: string;
  DB: string;
  course: string;
  first_name?: string;
  last_name?: string;
}

export interface SchedulerSection {
  section_id: string;
  course: string;
  instructor: string | null;
  slot: string | null;
  start: string;
  end: string;
  capacity: number;
  enrolled: number;
  students?: SectionStudent[];
}

export interface SchedulerStats {
  total_sections: number;
  total_registrations: number;
  students_with_preferred_slot: number;
  students_total: number;
  time_match_pct: number;
  unstaffed_sections: number;
  skipped_courses: string[];
}

export interface SchedulerRun {
  run_id: string;
  status: 'pending' | 'running' | 'complete' | 'failed' | 'cancelled';
  is_live?: boolean;
  term_start: string;
  term_end: string;
  term_name?: string;
  triggered_by: string;
  created_at: string;
  completed_at: string | null;
  stats: SchedulerStats | null;
  sections: SchedulerSection[];
  unmatched_students?: UnmatchedStudent[];
  /** The constraint values this run was actually given. Absent on runs created before it
   *  was recorded, so read it defensively. */
  constraints_used?: SchedulerConstraintsUsed;
  progress_steps: ProgressStep[];
  error: string | null;
}

export interface SchedulerConstraintsUsed {
  min_enroll: number;
  max_enroll: number;
  target_enroll: number;
  ft_max_sections: number;
  adjunct_max_sections: number;
}

export interface ProgressStep {
  step: number;
  label: string;
  status: 'pending' | 'running' | 'done' | 'failed';
  detail: string | null;
  completed_at: string | null;
}

export interface SchedulerConstraints {
  min_enroll: number;
  max_enroll: number;
  target_enroll: number;
  ft_max_sections: number;
  adjunct_max_sections: number;
}

export interface RunSchedulerRequest {
  term_start: string;
  term_end: string;
  term_name?: string;
  min_enroll?: number;
  max_enroll?: number;
  target_enroll?: number;
  force_touchdown?: boolean;
}

export interface InstructorScheduleResponse {
  instructor: string;
  email: string;
  term_start: string;
  term_end: string;
  sections: SchedulerSection[];
  message?: string;
}

// ---------------------------------------------------------------------------
// Data management types
// ---------------------------------------------------------------------------

export interface InstructorData {
  row_key: string;
  name: string;
  email: string;
  faculty_type: 'FT' | 'Adjunct';
  do_not_schedule: boolean;
  scheduling_notes: string | null;
  primary_area: string | null;
}

export interface CourseData {
  row_key: string;
  course_code: string;
  new_course_code: string | null;
  cengage_indicator: string;
}

export interface CredentialData {
  row_key: string;
  instructor_row_key: string;
  course_row_key: string;
  instructor_name: string;
  instructor_email: string;
  course_code: string;
}

export interface SlotPreferenceData {
  row_key: string;
  instructor_row_key: string;
  slot_row_key: string;
  preference: '1' | '2' | 'x' | '';
  instructor_name: string;
  instructor_email: string;
  slot_begin: string;
  slot_end: string;
}

export interface SlotInfo {
  row_key: string;
  begin: string;
  end: string;
}

export interface InstructorLookup {
  row_key: string;
  name: string;
  email: string;
}

export interface CourseLookup {
  row_key: string;
  code: string;
}

export interface ImpersonationEvent {
  id: string;
  admin_email: string;
  admin_name: string;
  instructor_email: string;
  instructor_name: string;
  started_at: string;
  action: 'start' | 'end';
}

export interface AuditEvent {
  id: string;
  admin_email: string;
  action: string;
  entity_type: string;
  entity_row_key: string;
  timestamp: string;
  before: string;
  after: string;
}

export interface PrerequisiteData {
  row_key: string;
  course_id: string;
  prereq_course_id: string;
}

export interface EquivalencyData {
  row_key: string;
  course_title: string;
  new_code: string;
  old_code: string;
}

export interface SequenceData {
  row_key: string;
  course_id: string;
  course_title: string;
  degree_program: string;
  sequence_number: number;
}

export interface SmsSyncStatus {
  sms_synced_at: string | null;
  sims_edited_at: string | null;
  bachelor_configured: boolean;
  nevada_configured: boolean;
  sync_duration_secs?: number | null;
  bachelor_rows?: number | null;
  nevada_rows?: number | null;
}

export interface SmsSyncResult {
  ok: boolean;
  already_current: boolean;
  message?: string;
  status?: string;
  sms_synced_at: string | null;
  rows_written: Record<string, number>;
  total_rows_written: number;
  started_at: string;
  completed_at: string;
  errors: string[] | null;
  phase?: string;
}

export interface InstructorSyncStatus {
  instructor_synced_at: string | null;
  sync_duration_secs?: number | null;
  rows_written?: number | null;
  bachelor_configured: boolean;
  nevada_configured: boolean;
  source?: string | null; // 'sms' | 'facman2'
}

export interface InstructorSyncResult {
  status: string;
  message?: string;
  started_at?: string;
  completed_at?: string;
  rows_written?: number;
  duration_secs?: number;
  errors?: string[] | null;
  phase?: string;
  source?: string; // 'sms' | 'facman2'
}

export interface PresenceUser {
  email: string;
  name: string;
  current_page: string;
  last_seen: string;
}

export interface PresenceResponse {
  users: PresenceUser[];
  fetched_at: string;
}

// ---------------------------------------------------------------------------

const SLOT_LABELS: Record<string, string> = {
  '1': '8:00 AM – 10:30 AM',
  '2': '11:00 AM – 1:30 PM',
  '3': '2:00 PM – 4:30 PM',
  '4': '5:00 PM – 7:30 PM',
  '5': '7:35 PM – 10:05 PM',
};

export const getSlotLabel = (slot: string | null): string =>
  slot ? (SLOT_LABELS[slot] ?? `Slot ${slot}`) : 'Unassigned';

export const schedulerApi = {
  // --- Run management ---
  runScheduler: (req: RunSchedulerRequest): Promise<SchedulerRun> =>
    axios.post('/scheduler/run', req).then(r => r.data),

  getResults: (): Promise<{ runs: SchedulerRun[] }> =>
    axios.get('/scheduler/results').then(r => r.data),

  getResult: (runId: string): Promise<SchedulerRun> =>
    axios.get(`/scheduler/results/${runId}`).then(r => r.data),

  cancelRun: (runId: string): Promise<{ ok: boolean; message: string; status?: string }> =>
    axios.post(`/scheduler/run/${runId}/cancel`).then(r => r.data),

  setLiveRun: (runId: string): Promise<{ ok: boolean; run_id: string; is_live: boolean }> =>
    axios.post(`/scheduler/run/${runId}/live`).then(r => r.data),

  unsetLiveRun: (runId: string): Promise<{ ok: boolean; run_id: string; is_live: boolean }> =>
    axios.delete(`/scheduler/run/${runId}/live`).then(r => r.data),

  deleteRun: (runId: string): Promise<{ ok: boolean; run_id: string }> =>
    axios.delete(`/scheduler/run/${runId}`).then(r => r.data),

  getInstructorSchedule: (): Promise<InstructorScheduleResponse> =>
    axios.get('/scheduler/instructor').then(r => r.data),

  getConstraints: (): Promise<SchedulerConstraints> =>
    axios.get('/scheduler/constraints').then(r => r.data),

  updateConstraints: (c: Partial<SchedulerConstraints>): Promise<SchedulerConstraints> =>
    axios.put('/scheduler/constraints', c).then(r => r.data),

  refreshData: (): Promise<{ message: string; refreshed_at: string; table_row_counts: Record<string, number> }> =>
    axios.post('/scheduler/data/refresh').then(r => r.data),

  // --- Data management ---
  getInstructors: (): Promise<{ instructors: InstructorData[] }> =>
    axios.get('/scheduler/data/instructors').then(r => r.data),

  updateInstructor: (rowKey: string, fields: Partial<Pick<InstructorData, 'do_not_schedule' | 'scheduling_notes' | 'faculty_type' | 'name' | 'primary_area'>>): Promise<{ ok: boolean }> =>
    axios.put(`/scheduler/data/instructors/${rowKey}`, fields).then(r => r.data),

  getCourses: (): Promise<{ courses: CourseData[] }> =>
    axios.get('/scheduler/data/courses').then(r => r.data),

  getCredentials: (): Promise<{
    credentials: CredentialData[];
    instructors: InstructorLookup[];
    courses: CourseLookup[];
  }> =>
    axios.get('/scheduler/data/credentials').then(r => r.data),

  addCredential: (instructorRowKey: string, courseRowKey: string): Promise<{ ok: boolean; row_key: string }> =>
    axios.post('/scheduler/data/credentials', { instructor_row_key: instructorRowKey, course_row_key: courseRowKey }).then(r => r.data),

  deleteCredential: (rowKey: string): Promise<{ ok: boolean }> =>
    axios.delete(`/scheduler/data/credentials/${rowKey}`).then(r => r.data),

  getSlotPreferences: (): Promise<{
    preferences: SlotPreferenceData[];
    slots: SlotInfo[];
    instructors: InstructorLookup[];
  }> =>
    axios.get('/scheduler/data/slots').then(r => r.data),

  upsertSlotPreference: (instructorRowKey: string, slotRowKey: string, preference: '1' | '2' | 'x' | ''): Promise<{ ok: boolean; action: string }> =>
    axios.post('/scheduler/data/slots', { instructor_row_key: instructorRowKey, slot_row_key: slotRowKey, preference }).then(r => r.data),

  getPrerequisites: (): Promise<{ prerequisites: PrerequisiteData[]; course_codes: string[] }> =>
    axios.get('/scheduler/data/prerequisites').then(r => r.data),

  addPrerequisite: (courseId: string, prereqCourseId: string): Promise<{ ok: boolean; row_key: string }> =>
    axios.post('/scheduler/data/prerequisites', { course_id: courseId, prereq_course_id: prereqCourseId }).then(r => r.data),

  deletePrerequisite: (rowKey: string): Promise<{ ok: boolean }> =>
    axios.delete(`/scheduler/data/prerequisites/${rowKey}`).then(r => r.data),

  getEquivalencies: (): Promise<{ equivalencies: EquivalencyData[] }> =>
    axios.get('/scheduler/data/equivalencies').then(r => r.data),

  addEquivalency: (newCode: string, oldCode: string, courseTitle: string): Promise<{ ok: boolean; row_key: string }> =>
    axios.post('/scheduler/data/equivalencies', { new_code: newCode, old_code: oldCode, course_title: courseTitle }).then(r => r.data),

  deleteEquivalency: (rowKey: string): Promise<{ ok: boolean }> =>
    axios.delete(`/scheduler/data/equivalencies/${rowKey}`).then(r => r.data),

  getSequences: (): Promise<{ sequences: SequenceData[]; programs: string[] }> =>
    axios.get('/scheduler/data/sequences').then(r => r.data),

  // --- Impersonation ---
  getInstructorScheduleAs: (asEmail: string): Promise<InstructorScheduleResponse> =>
    axios.get('/scheduler/instructor', { params: { as_email: asEmail } }).then(r => r.data),

  logImpersonation: (data: { instructor_email: string; instructor_name: string; action: 'start' | 'end' }): Promise<{ ok: boolean; id: string }> =>
    axios.post('/scheduler/impersonation/log', data).then(r => r.data),

  getImpersonationLog: (): Promise<{ events: ImpersonationEvent[] }> =>
    axios.get('/scheduler/impersonation/log').then(r => r.data),

  getAuditLog: (): Promise<{ events: AuditEvent[] }> =>
    axios.get('/scheduler/data/audit').then(r => r.data),

  // --- SMS sync ---
  getSmsSyncStatus: (): Promise<SmsSyncStatus> =>
    axios.get('/scheduler/sms/sync/status').then(r => r.data),

  getSmsSyncProgress: (): Promise<SmsSyncResult> =>
    axios.get('/scheduler/sms/sync/progress').then(r => r.data),

  triggerSmsSync: (): Promise<SmsSyncResult> =>
    axios.post('/scheduler/sms/sync').then(r => r.data),

  // --- Instructor sync ---
  getInstructorSyncStatus: (): Promise<InstructorSyncStatus> =>
    axios.get('/scheduler/sms/instructor/sync/status').then(r => r.data),

  getInstructorSyncProgress: (): Promise<InstructorSyncResult> =>
    axios.get('/scheduler/sms/instructor/sync/progress').then(r => r.data),

  triggerInstructorSync: (): Promise<InstructorSyncResult> =>
    axios.post('/scheduler/sms/instructor/sync').then(r => r.data),

  // --- Presence ---
  presenceHeartbeat: (page: string): Promise<{ ok: boolean }> =>
    axios.post('/presence/heartbeat', { page }).then(r => r.data),

  getActiveUsers: (): Promise<PresenceResponse> =>
    axios.get('/presence').then(r => r.data),

  // --- Matrix editing ---
  moveStudent: (
    runId: string,
    payload: { student_no: string; db: string; from_section_id: string; to_section_id: string }
  ): Promise<{ ok: boolean; run_id: string; excel_url: string | null }> =>
    axios.patch(`/scheduler/run/${runId}/move-student`, payload).then(r => r.data),

  assignStudent: (
    runId: string,
    payload: { student_no: string; db: string; course: string; to_section_id: string }
  ): Promise<{ ok: boolean; run_id: string; excel_url: string | null }> =>
    axios.patch(`/scheduler/run/${runId}/assign-student`, payload).then(r => r.data),

  moveSection: (
    runId: string,
    payload: { section_id: string; new_instructor: string; new_slot: string }
  ): Promise<{ ok: boolean; run_id: string; excel_url: string | null }> =>
    axios.patch(`/scheduler/run/${runId}/move-section`, payload).then(r => r.data),

  deleteSection: (
    runId: string,
    sectionId: string,
    force?: boolean
  ): Promise<{ ok: boolean; run_id: string; section_id: string; displaced_students: number; excel_url: string | null }> =>
    axios.delete(`/scheduler/run/${runId}/section/${sectionId}`, { data: { force: !!force } }).then(r => r.data),

  createSection: (
    runId: string,
    payload: { course: string; instructor?: string | null; slot?: string | null; capacity?: number }
  ): Promise<{ ok: boolean; run_id: string; section: SchedulerSection; excel_url: string | null }> =>
    axios.post(`/scheduler/run/${runId}/section`, payload).then(r => r.data),

  getExcelUrl: (runId: string): Promise<{ url: string | null }> =>
    axios.get(`/scheduler/run/${runId}/excel`).then(r => r.data),
};

